import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { Aggregate, Filter, GroupFilterMode } from './types';
import { aggregateContent } from './api';
import { getAggregateString } from './query-formatters';
import type { ContentType } from '@ilteducation/content-registry-types';

type AggregateContextState = {
  contentType: ContentType;
  fields: string[];
  filter: Filter;
  isLoading: boolean;
  aggregation: Aggregate[];
};

type State = {
  [contextId: string]: AggregateContextState | undefined;
};

const useAggregateContexts = (contentType: ContentType) =>
  defineStore(`content-aggregate-contexts-${contentType}`, () => {
    const contextState = ref<State>({});

    const context = computed(() => (contextId: string) => contextState.value[contextId]);
    const result = computed(() => (contextId: string) => contextState.value[contextId]?.aggregation ?? []);
    const isLoading = computed(() => (contextId: string) => contextState.value[contextId]?.isLoading ?? false);
    const contextExists = computed(() => (contextId: string) => contextState.value[contextId] !== undefined);

    const createAggrId = (fields: string[], filter: Filter) =>
      `${contentType}:${getAggregateString(fields, filter).slice(1)}`; // doesn't take into size or groupFilterMode into account for assumed uniqueness per query

    const aggregate = (fields: string[], filter: Filter, size?: number, groupFilterMode?: GroupFilterMode) => {
      const contextId = createAggrId(fields, filter);
      if (contextExists.value(contextId)) {
        // is already loading or has been loaded
        return;
      }

      contextState.value = {
        ...contextState.value,
        ...{
          [contextId]: {
            contentType,
            fields,
            filter,
            isLoading: true,
            aggregation: [],
          },
        },
      };

      aggregateContent(contentType, fields, filter, size, groupFilterMode)
        .then(({ aggregates }) => {
          contextState.value = {
            ...contextState.value,
            ...{
              [contextId]: {
                contentType,
                fields,
                filter,
                isLoading: false,
                aggregation: aggregates,
              },
            },
          };
        })
        .catch(() => {
          // in case of failed retrieval, to allow for re-retrieval
          const newState = { ...contextState.value };
          delete newState[contextId];
          contextState.value = newState;
        });
    };

    const clearAggr = () => {
      contextState.value = {};
    };

    return {
      createAggrId,
      aggregate,
      clearAggr,
      context,
      result,
      isLoading,
    };
  })();

export default useAggregateContexts;
